import { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { cn } from '../lib/utils';
import { ICON, IconButton } from './ui';

const VISIT_COUNT_KEY = 'trigion.pwa.visit_count';
const DISMISSED_KEY = 'trigion.pwa.install_dismissed';
const INSTALLED_KEY = 'trigion.pwa.installed';
const MIN_VISITS = 5;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia?.('(display-mode: standalone)').matches;
  // iOS legacy flag
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return Boolean(mq || iosStandalone);
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !/MSStream/.test(ua);
}

function bumpVisitCount(): number {
  try {
    const n = Number(localStorage.getItem(VISIT_COUNT_KEY) || '0') + 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}

export function InstallPromptBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {}
      return;
    }
    try {
      if (localStorage.getItem(INSTALLED_KEY) === '1') return;
      if (localStorage.getItem(DISMISSED_KEY) === '1') return;
    } catch {}

    const visits = bumpVisitCount();
    const eligible = visits >= MIN_VISITS;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (eligible) setVisible(true);
    };

    const onAppInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {}
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);

    // iOS heeft geen beforeinstallprompt — toon eigen Add-to-Home-Screen hint.
    if (isIOS() && eligible) {
      setShowIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {}
      } else {
        try { localStorage.setItem(DISMISSED_KEY, '1'); } catch {}
      }
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="card enter flex items-start gap-3">
      <Smartphone className={cn(ICON.md, 'mt-0.5 shrink-0 text-slate-400')} aria-hidden />
      <div className="min-w-0 flex-1">
        <h2>App installeren</h2>
        {showIosHint ? (
          <p className="t-muted mt-0.5">
            Tik in Safari op Delen en kies <span className="text-slate-200">Zet op beginscherm</span>.
          </p>
        ) : (
          <p className="t-muted mt-0.5">Zet Trigion op je beginscherm, dan opent hij als gewone app.</p>
        )}
        {!showIosHint && deferredPrompt && (
          <button onClick={install} className="btn-secondary mt-3">
            <Download className={ICON.sm} aria-hidden /> Installeren
          </button>
        )}
      </div>
      <IconButton icon={X} label="Sluiten" onClick={dismiss} className="-mr-2 -mt-2 shrink-0" />
    </div>
  );
}
